import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import { formatQuestionnaireScoreValue } from 'src/app/utils/questionnaire-history-score';

interface MotricityIndexScoreField {
  controlName: string;
  // A limb adds up its three tests + 1; a side is the mean of its two limbs.
  isSide: boolean;
  // The tests of a limb, or the limb scores of a side.
  parts: string[];
}

// The points are the number in "(n)" at the start of the answer.
const MOTRICITY_INDEX_POINTS_REGEX = /^\s*\(\s*(\d+)\s*\)/;

// Pinch grip has values of its own. Both sets hold a 19 for different
// movements, which is harmless because only the points are added up.
const MOTRICITY_INDEX_PINCH_GRIP_ITEMS = ['question897', 'question909'];
const MOTRICITY_INDEX_PINCH_GRIP_POINTS = [0, 11, 19, 22, 26, 33];
const MOTRICITY_INDEX_MOVEMENT_POINTS = [0, 9, 14, 19, 25, 33];

// The +1 makes a normal limb 100 rather than 99, so a side tops out at 100 too.
const MOTRICITY_INDEX_MAX_SCORE = 100;

// Titles separate the instruction with "||" or "‖".
const MOTRICITY_INDEX_TITLE_SEPARATOR = /\|\||‖/;

// RIGHT SIDE and LEFT SIDE. The Arm and Leg sections inside them repeat on
// both sides and only label their tests.
const MOTRICITY_INDEX_SIDE_SECTIONS = ['question895', 'question907'];

// In calculation order: a side comes after its arm and leg. There is no
// total across both sides: one would hide the asymmetry the scale measures.
const MOTRICITY_INDEX_SCORE_FIELDS: MotricityIndexScoreField[] = [
  // Right arm: pinch grip, elbow flexion, shoulder abduction
  {
    controlName: 'question900',
    isSide: false,
    parts: ['question897', 'question898', 'question899'],
  },
  // Right leg: ankle dorsiflexion, knee extension, hip flexion
  {
    controlName: 'question905',
    isSide: false,
    parts: ['question902', 'question903', 'question904'],
  },
  // Right side
  {
    controlName: 'question906',
    isSide: true,
    parts: ['question900', 'question905'],
  },
  // Left arm
  {
    controlName: 'question912',
    isSide: false,
    parts: ['question909', 'question910', 'question911'],
  },
  // Left leg
  {
    controlName: 'question917',
    isSide: false,
    parts: ['question914', 'question915', 'question916'],
  },
  // Left side
  {
    controlName: 'question918',
    isSide: true,
    parts: ['question912', 'question917'],
  },
];

@Component({
  selector: 'app-motricity-index-questions',
  templateUrl: './motricity-index-questions.component.html',
  styleUrl: './motricity-index-questions.component.css',
  standalone: false,
})
export class MotricityIndexQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  // The page title holds the scale's heading and instruction.
  @Input() pageTitle = '';
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  readonly maxScore = MOTRICITY_INDEX_MAX_SCORE;

  private sub?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateScores();
    });
    this.recalculateScores();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return title?.split(MOTRICITY_INDEX_TITLE_SEPARATOR)[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split(MOTRICITY_INDEX_TITLE_SEPARATOR)[1]?.trim() ?? '';
  }

  isSideSection(element: IQuestionnaireQuestion): boolean {
    return MOTRICITY_INDEX_SIDE_SECTIONS.includes(element.name);
  }

  isScoreElement(element: IQuestionnaireQuestion): boolean {
    return element.type === 'text' && !!this.getScoreField(element.name);
  }

  isSideScoreElement(element: IQuestionnaireQuestion): boolean {
    return this.getScoreField(element.name)?.isSide ?? false;
  }

  getScoreDisplay(controlName: string): string {
    const value = this.formGroup.get(controlName)?.value;
    return value === null || value === undefined || value === ''
      ? '0'
      : String(value);
  }

  private getScoreField(
    controlName: string,
  ): MotricityIndexScoreField | undefined {
    return MOTRICITY_INDEX_SCORE_FIELDS.find(
      (field) => field.controlName === controlName,
    );
  }

  private recalculateScores(): void {
    const scores = new Map<string, number | null>();

    for (const field of MOTRICITY_INDEX_SCORE_FIELDS) {
      const score = field.isSide
        ? this.calculateSide(
            field.parts.map((limb) => scores.get(limb) ?? null),
          )
        : this.calculateLimb(field.parts);

      scores.set(field.controlName, score);
      this.setLockedScore(field.controlName, score);
    }

    this.cdr.detectChanges();
  }

  // Live sum of the tests answered so far + 1, or null while none is, so an
  // untouched limb stays empty and is not saved.
  private calculateLimb(tests: string[]): number | null {
    const points = tests
      .map((test) => this.getSelectedPoints(test))
      .filter((point): point is number => point !== null);

    return points.length
      ? points.reduce((sum, point) => sum + point, 0) + 1
      : null;
  }

  // The mean of the arm and the leg, an empty limb counting the 0 it shows,
  // or null while both are empty. It can end in .5.
  private calculateSide(limbs: (number | null)[]): number | null {
    return limbs.some((limb) => limb !== null)
      ? limbs.reduce<number>((sum, limb) => sum + (limb ?? 0), 0) /
          limbs.length
      : null;
  }

  // Points come from the selected choice text, never from its position, and
  // must be one of the values of that test's option set.
  private getSelectedPoints(controlName: string): number | null {
    const selectedValue = this.formGroup.get(controlName)?.value;
    if (
      selectedValue === null ||
      selectedValue === undefined ||
      selectedValue === ''
    ) {
      return null;
    }

    const choice = (
      this.elements?.find((el) => el.name === controlName)?.choices ?? []
    ).find((c: IQuestionnaireChoice) => c.value === selectedValue);
    const match = (choice?.text ?? '').match(MOTRICITY_INDEX_POINTS_REGEX);
    const points = match ? Number(match[1]) : null;
    const validPoints = MOTRICITY_INDEX_PINCH_GRIP_ITEMS.includes(controlName)
      ? MOTRICITY_INDEX_PINCH_GRIP_POINTS
      : MOTRICITY_INDEX_MOVEMENT_POINTS;

    return points !== null && validPoints.includes(points) ? points : null;
  }

  private setLockedScore(controlName: string, score: number | null): void {
    const control = this.formGroup.get(controlName);
    if (!control) return;

    control.setValue(
      score === null ? '' : formatQuestionnaireScoreValue(score),
      { emitEvent: false },
    );
    control.disable({ emitEvent: false });
  }
}
